import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isPublicAgencyId } from './agencyUrl';

const EMPTY = [];
const BATCH_SIZE = 100;
const REFRESH_INTERVAL = 60000;

export async function fetchAvailableAgencies(publicIds, { signal } = {}) {
    const apiUrl = process.env.REACT_APP_AUTH_API_URL?.trim();
    if (!apiUrl) throw new Error('Agency availability API is not configured.');
    const batches = [];
    for (let index = 0; index < publicIds.length; index += BATCH_SIZE) {
        batches.push(publicIds.slice(index, index + BATCH_SIZE));
    }
    const results = await Promise.all(batches.map(async requestedIds => {
        const response = await fetch(`${apiUrl.replace(/\/+$/, '')}/auth/getAvailableFuneralHonors`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'omit',
            cache: 'no-store',
            signal,
            body: JSON.stringify({ publicIds: requestedIds }),
        });
        if (!response.ok) throw new Error('Agency availability request failed.');
        const result = await response.json();
        if (!Array.isArray(result?.publicIds)
            || result.publicIds.some(id => !isPublicAgencyId(id) || !requestedIds.includes(id))
            || new Set(result.publicIds).size !== result.publicIds.length) {
            throw new Error('Invalid agency availability response.');
        }
        // Reviews belong to this live availability response, never to saved cards.
        // A missing or malformed optional map must not hide otherwise valid agencies.
        const googleReviews = {};
        if (result.googleReviews && typeof result.googleReviews === 'object' && !Array.isArray(result.googleReviews)) {
            result.publicIds.forEach(id => {
                if (!Object.prototype.hasOwnProperty.call(result.googleReviews, id)) return;
                const reviews = result.googleReviews[id];
                googleReviews[id] = reviews && typeof reviews === 'object' && !Array.isArray(reviews)
                    ? Object.fromEntries(['placeId', 'rating', 'reviewCount', 'url'].map(field => [field, reviews[field] ?? null]))
                    : null;
            });
        }
        return { publicIds: result.publicIds, googleReviews };
    }));
    return {
        publicIds: results.flatMap(result => result.publicIds),
        googleReviews: Object.assign({}, ...results.map(result => result.googleReviews)),
    };
}

export async function fetchAvailableAgencyIds(publicIds, options) {
    const result = await fetchAvailableAgencies(publicIds, options);
    return result.publicIds;
}

// Saved presentation data never establishes whether an agency is still public.
export default function useAvailableAgencies(cards) {
    const idsKey = JSON.stringify([...new Set(cards.map(card => card?.publicId).filter(isPublicAgencyId))].sort());
    const requestedIds = useMemo(() => JSON.parse(idsKey), [idsKey]);
    const [revision, setRevision] = useState(0);
    const [check, setCheck] = useState({ requestedIds: EMPTY, publicIds: EMPTY, revision: -1, status: 'idle' });
    const confirmedRef = useRef(null);
    const retry = useCallback(() => setRevision(value => value + 1), []);

    useEffect(() => {
        if (!requestedIds.length) return undefined;
        if (document.visibilityState === 'hidden') return undefined;
        // Removing rejected or manually deleted cards cannot invalidate the check of the remaining IDs.
        const confirmed = confirmedRef.current;
        if (confirmed?.revision === revision && requestedIds.every(id => confirmed.requestedIds.includes(id))) {
            setCheck(previous => previous === confirmed ? previous : confirmed);
            return undefined;
        }
        const controller = new AbortController();
        let cancelled = false;
        const timeout = window.setTimeout(() => controller.abort(), 20000);
        setCheck({ requestedIds, publicIds: EMPTY, revision, status: 'loading' });
        fetchAvailableAgencies(requestedIds, { signal: controller.signal }).then(({ publicIds, googleReviews }) => {
            if (cancelled || controller.signal.aborted) return;
            const next = { requestedIds, publicIds, googleReviews, revision, status: 'ready' };
            confirmedRef.current = next;
            setCheck(next);
        }).catch(() => {
            if (!cancelled) setCheck({ requestedIds, publicIds: EMPTY, revision, status: 'error' });
        }).finally(() => window.clearTimeout(timeout));
        return () => {
            cancelled = true;
            controller.abort();
            window.clearTimeout(timeout);
        };
    }, [requestedIds, revision]);

    useEffect(() => {
        const refresh = () => {
            if (document.visibilityState !== 'hidden') retry();
        };
        window.addEventListener('focus', refresh);
        window.addEventListener('pageshow', refresh);
        // Hide public contacts as soon as the document leaves view, including a saved BFCache document.
        document.addEventListener('visibilitychange', retry);
        const interval = window.setInterval(refresh, REFRESH_INTERVAL);
        return () => {
            window.removeEventListener('focus', refresh);
            window.removeEventListener('pageshow', refresh);
            document.removeEventListener('visibilitychange', retry);
            window.clearInterval(interval);
        };
    }, [retry]);

    const covered = check.revision === revision && requestedIds.every(id => check.requestedIds.includes(id));
    const ready = covered && check.status === 'ready';
    const availableCards = useMemo(() => ready
        ? cards.filter(card => check.publicIds.includes(card?.publicId)).map(({ googleReviews: savedReviews, ...card }) =>
            Object.prototype.hasOwnProperty.call(check.googleReviews, card.publicId)
                ? { ...card, googleReviews: check.googleReviews[card.publicId] } : card)
        : EMPTY, [cards, check.publicIds, check.googleReviews, ready]);
    const unavailableIds = useMemo(() => ready
        ? requestedIds.filter(id => !check.publicIds.includes(id)) : EMPTY, [requestedIds, check.publicIds, ready]);

    return {
        cards: availableCards,
        loading: requestedIds.length > 0 && (!covered || check.status === 'loading'),
        error: requestedIds.length > 0 && covered && check.status === 'error',
        retry,
        unavailableIds,
    };
}
